import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from 'src/app/store.service';
import { UtilitiesService } from 'src/app/core/utilities.service';

@Component({
  selector: 'app-ai-landing-page-compare',
  templateUrl: './ai-landing-page-compare.component.html',
  styleUrls: ['./ai-landing-page-compare.component.scss']
})
export class AiLandingPageCompareComponent implements OnInit {

  @ViewChild('editPlanModal') editPlanModal!: TemplateRef<any>;

  yourLandingPage = '';
  competitorLandingPage = '';
  isSubmitting = false;
  isApplying = false;
  applyingType: number | null = null;
  analysis: any = null;
  showApplyButton = false;
  comparisonId: string | null = null;
  actionPlanOpen: { [key: string]: boolean } = {};

  editPlanItem: any = null;
  editPlanContent = '';
  isUpdatingPlan = false;

  constructor(
    private svc: StoreService,
    private utils: UtilitiesService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void { }

  onSubmit(): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';

    this.isSubmitting = true;
    this.analysis = null;
    this.showApplyButton = false;
    this.comparisonId = null;

    this.svc.compareLandingPage(bearer, storeId, this.yourLandingPage, this.competitorLandingPage)
      .subscribe({
        next: (res: any) => {
          this.isSubmitting = false;
          const data = res?.data ?? res;

          this.comparisonId = res?.data?.comparison_id ?? null;

          const urlLookup = data?.url_lookup;
          const isApplied = data?.is_apply;
          this.showApplyButton = (isApplied == null || isApplied == 0) && (!urlLookup || (!!urlLookup.same_store && urlLookup.editor === 'html'));

          this.analysis = data?.analysis ?? data;
          this.normalizeAnalysis();
        },
        error: (err: any) => {
          this.isSubmitting = false;
          const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Something went wrong. Please try again.';
          this.utils.msgBox.open({
            title: this.utils.translate('Error'),
            message: this.utils.translate(msg),
            icon: 'error',
            buttonList: [
              { type: 'spacing' },
              { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
            ],
          });
        }
      });
  }

  onApply(apply: number): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    const storeId = localStorage.getItem('store_id') ?? '';

    this.isApplying = true;
    this.applyingType = apply;
    this.svc.applyLandingPageUpdate(bearer, this.comparisonId ?? '', storeId, apply)
      .subscribe({
        next: () => {
          this.isApplying = false;
          this.applyingType = null;
          this.showApplyButton = false;
          this.utils.msgBox.open({
            title: this.utils.translate('Success'),
            message: this.utils.translate('Your landing page has been optimised based on the AI recommendations.'),
            icon: 'success',
            buttonList: [
              { type: 'spacing' },
              { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
            ],
          });
        },
        error: (err: any) => {
          this.isApplying = false;
          this.applyingType = null;
          const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to apply changes. Please try again.';
          this.utils.msgBox.open({
            title: this.utils.translate('Error'),
            message: this.utils.translate(msg),
            icon: 'error',
            buttonList: [
              { type: 'spacing' },
              { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
            ],
          });
        }
      });
  }

  openEditPlan(item: any): void {
    this.editPlanItem = item;
    this.editPlanContent = item.new_content ?? '';
    this.modalService.open(this.editPlanModal, { size: 'md' });
  }

  onUpdatePlan(modal: any): void {
    const bearer = localStorage.getItem('bearer') ?? '';
    this.isUpdatingPlan = true;
    this.svc.updateActionPlan(bearer, this.comparisonId ?? '', this.editPlanItem.day, { content: this.editPlanContent })
      .subscribe({
        next: () => {
          this.isUpdatingPlan = false;
          this.editPlanItem.new_content = this.editPlanContent;
          modal.close();
        },
        error: (err: any) => {
          this.isUpdatingPlan = false;
          const msg = err?.error?.message?.error ?? err?.error?.message ?? 'Failed to update. Please try again.';
          this.utils.msgBox.open({
            title: this.utils.translate('Error'),
            message: this.utils.translate(msg),
            icon: 'error',
            buttonList: [
              { type: 'spacing' },
              { text: this.utils.translate('OK'), style: 'primary', onClick: 'close' },
            ],
          });
        }
      });
  }

  private normalizeAnalysis(): void {
    if (!this.analysis) return;
    const r = this.analysis.readability;
    if (r?.dealer) {
      if (r.dealer.competitor && !r.competitor) {
        r.competitor = r.dealer.competitor;
        if (!r.recommendation) r.recommendation = r.dealer.competitor.recommendation;
      }
      if (r.dealer.action_plan && this.isEmptyOrMissing(this.analysis.action_plan)) {
        this.analysis.action_plan = r.dealer.action_plan;
      }
      if (r.dealer.rewritten_copy && !this.analysis.rewritten_copy) {
        this.analysis.rewritten_copy = r.dealer.rewritten_copy;
      }
    }
  }

  private isEmptyOrMissing(val: any): boolean {
    if (!val) return true;
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'object') return Object.keys(val).length === 0;
    return false;
  }

  getActionPlanPhases(actionPlan: any): { key: string; label: string; items: any[] }[] {
    if (!actionPlan) return [];
    return Object.entries(actionPlan).map(([key, items]) => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/(\d+)/g, ' $1').trim(),
      items: items as any[]
    }));
  }

  togglePhase(key: string): void {
    this.actionPlanOpen[key] = !this.actionPlanOpen[key];
  }

}
